// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { getSelectedImage } from "@insite/client-framework/Store/Pages/ProductDetails/ProductDetailsSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductImageCarousel, {
    ProductImageCarouselStyles,
} from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Badge from "@insite/mobius/Badge";
import Hidden from "@insite/mobius/Hidden";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { parseProductCategories } from "@sc/XDL/Utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps &
    HasProductContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProductContext) => {
    const session = getSession(state);
    return {
        selectedImage: getSelectedImage(state, ownProps.productContext.product),
        selectedImageIndex: state.pages.productDetails.selectedImageIndex,
        // xng-change
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        currencyCode: session.currency?.currencyCode,
        locationId: session.pickUpWarehouse?.id,
    };
};

const mapDispatchToProps = {
    setSelectedImageIndex,
};

export interface ProductDetailsPrimaryImageStyles {
    /**
     * @deprecated Use primaryImage styles instead.
     */
    centeringWrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    image?: LazyImageProps;

    primaryImage?: ProductPrimaryImageStyles;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    imageCarousel?: ProductImageCarouselStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
        img: {
            css: css`
                width: auto;
                max-height: 70vh;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const styles = primaryImageStyles;

const ProductDetailsPrimaryImage = ({
    productContext,
    selectedImage,
    selectedImageIndex,
    setSelectedImageIndex,
    // xng-change
    xGoogleAnalytics,
    currencyCode,
    locationId,
}: Props) => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = useState(false);
    const imageClickHandler = () => {
        // XNG-Change
        if (xGoogleAnalytics?.pdpImageClickEvent && xGoogleAnalytics?.pdpImageClickEventName) {
            const dataLayer = getDataLayer();
            if (dataLayer && productContext.product) {
                const { product } = productContext;

                const googleAnalyticsData = {
                    event: xGoogleAnalytics.pdpImageClickEventName,
                    ecommerce: {
                        click_text: product?.id,
                        currency: currencyCode,
                        value: product?.quoteRequired
                            ? "Requires Quote"
                            : productContext.productInfo?.pricing?.unitNetPrice || 0,
                        affiliation: xGoogleAnalytics.affiliation || "",
                        bill_to_num: xGoogleAnalytics.billToNum || "",
                        ship_to_num: xGoogleAnalytics.shipToNum || "",
                        is_rep: xGoogleAnalytics.isRep || "",
                        cust_type: xGoogleAnalytics.customerType || "",
                        login_type: xGoogleAnalytics.loginType || "",
                        warehouse: xGoogleAnalytics.warehouse || "",
                        sales_rep: xGoogleAnalytics.salesRep || "",
                        items: [
                            {
                                item_name: product?.productTitle,
                                item_id: product?.productNumber,
                                item_variant: "",
                                items_available: productContext.productInfo?.inventory?.qtyOnHand || 0,
                                price: productContext.productInfo?.pricing?.unitNetPrice || 0,
                                discount: productContext.productInfo?.pricing
                                    ? productContext.productInfo.pricing.unitNetPrice <
                                      productContext.productInfo.pricing.unitListPrice
                                        ? productContext.productInfo.pricing.unitListPrice -
                                          productContext.productInfo.pricing.unitNetPrice
                                        : 0
                                    : 0,
                                index: 0,
                                item_list_name: "Product Detail Page",
                                item_list_id: xGoogleAnalytics["includeProductCategory"]
                                    ? product?.properties?.["categoryId"] || ""
                                    : undefined,
                                affiliation: xGoogleAnalytics.affiliation || "",
                                quantity: product?.minimumOrderQty || 1,
                                default_state: "No UOM selected",
                                location_id: locationId,
                                item_brand: xGoogleAnalytics["includeProductBrand"]
                                    ? product?.brand?.name || ""
                                    : undefined,
                                ...parseProductCategories(
                                    product?.properties?.itemCategoriesShortDescription,
                                    true,
                                    xGoogleAnalytics["includeProductCategory"],
                                ),
                            },
                        ],
                    },
                };

                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }
        }
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const { product } = productContext;
    if (!product) {
        return null;
    }

    const image =
        selectedImage ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
        styles,
    });

    const path = image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    return (
        <>
            {!showOverlayBadge && (
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    onClick={imageClickHandler}
                    extendedStyles={styles.primaryImage}
                    // XNG-Changes
                    useLargeImage={true}
                />
            )}
            {showOverlayBadge && overlayBadge && (
                <StyledWrapper {...styles.badgeAreaWrapper} ref={badgeAreaWrapperRef}>
                    <ProductPrimaryImage
                        product={product}
                        image={image}
                        onClick={imageClickHandler}
                        extendedStyles={styles.primaryImage}
                        // XNG-Changes
                        useLargeImage={true}
                    />
                    <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                        <Badge
                            type={overlayBadge.badgeType}
                            value={
                                overlayBadge.badgeType === "Image"
                                    ? overlayBadge.largeImageBadgePath
                                    : overlayBadge.displayText
                            }
                            badgeTextColor={overlayBadge.textColorHexCode}
                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                            textBadgeCss={styles.badgeText}
                            imageBadgeProps={styles.badgeImageProps}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <Modal
                {...styles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
                headline={translate("Product Image")}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={styles.fullPrimaryImage}
                />
                {/* XNG-Change not included in invision */}
                {/* {product.images && product.images.length > 1 && (
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        onSelectImage={selectImageHandler}
                        extendedStyles={styles.imageCarousel}
                    />
                )} */}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPrimaryImage)),
    definition: {
        displayName: "Primary Image",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
